import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Card,
  Grid,
  Box,
  Chip,
  TextField,
  InputAdornment,
  Paper,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  Autocomplete,
  alpha,
  useTheme,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Refresh,
  WhatsApp,
  Search,
  AccessTime,
  FilterAlt,
  CheckCircle,
  Cancel,
  Storage,
  AddBusiness,
  Timeline,
  Group,
} from "@mui/icons-material";
import MDBox from "shared/components/MDBox";
import MDTypography from "shared/components/MDTypography";
import MDButton from "shared/components/MDButton";

const HeaderWhatsappCop = ({
  onRefresh,
  onCreateClick,
  loading = false,
  filtros = { grupo: "todos", activo: "todos", pulso: "todos", busqueda: "" },
  onFiltroChange,
  gruposNOC = [],
}) => {
  const theme = useTheme();
  const [searchInput, setSearchInput] = useState(filtros.busqueda || "");
  const [filtroActivo, setFiltroActivo] = useState(filtros.activo || "todos");
  const [filtroPulso, setFiltroPulso] = useState(filtros.pulso || "todos");
  const [filtroGrupo, setFiltroGrupo] = useState(filtros.grupo || "todos");
  const [selectedGrupo, setSelectedGrupo] = useState(null);
  const [grupoInputValue, setGrupoInputValue] = useState("");

  // Opciones para el Autocomplete de grupos NOC
  const grupoOptions = [
    { value: "todos", label: "Todos los grupos", color: "#757575" },
    ...gruposNOC.map((grupo) => ({
      value: grupo,
      label: grupo,
      color: getGrupoColor(grupo),
    })),
  ];

  // Función para asignar colores según el grupo NOC
  function getGrupoColor(grupo) {
    const grupoLower = grupo?.toLowerCase() || "";
    if (grupoLower.includes("terminal")) return "#5c6bc0";
    if (grupoLower.includes("centro comercial")) return "#66bb6a";
    if (grupoLower.includes("tequendama")) return "#ffa726";
    if (grupoLower.includes("integracion")) return "#42a5f5";
    return "#78909c";
  }

  // Sincronizar con props
  useEffect(() => {
    setSearchInput(filtros.busqueda || "");
    setFiltroActivo(filtros.activo || "todos");
    setFiltroPulso(filtros.pulso || "todos");
    setFiltroGrupo(filtros.grupo || "todos");

    if (filtros.grupo && filtros.grupo !== "todos") {
      setSelectedGrupo({ value: filtros.grupo, label: filtros.grupo });
    } else {
      setSelectedGrupo(null);
    }
  }, [filtros]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchInput(value);
    if (onFiltroChange) {
      onFiltroChange("busqueda", value);
    }
  };

  const handleActivoChange = (event, newValue) => {
    if (newValue !== null) {
      setFiltroActivo(newValue);
      if (onFiltroChange) {
        onFiltroChange("activo", newValue);
      }
    }
  };

  const handlePulsoChange = (event, newValue) => {
    if (newValue !== null) {
      setFiltroPulso(newValue);
      if (onFiltroChange) {
        onFiltroChange("pulso", newValue);
      }
    }
  };

  const handleGrupoChange = (event, newValue) => {
    setSelectedGrupo(newValue);
    const grupoValue = newValue ? newValue.value : "todos";
    setFiltroGrupo(grupoValue);
    if (onFiltroChange) {
      onFiltroChange("grupo", grupoValue);
    }
  };

  const clearSearch = () => {
    setSearchInput("");
    if (onFiltroChange) {
      onFiltroChange("busqueda", "");
    }
  };

  const clearAllFilters = () => {
    setSearchInput("");
    setFiltroActivo("todos");
    setFiltroPulso("todos");
    setSelectedGrupo(null);
    setFiltroGrupo("todos");
    if (onFiltroChange) {
      onFiltroChange("busqueda", "");
      onFiltroChange("activo", "todos");
      onFiltroChange("pulso", "todos");
      onFiltroChange("grupo", "todos");
    }
  };

  const formatTime = () => {
    const now = new Date();
    return now.toLocaleTimeString("es-ES", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
  };

  const hasActiveFilters =
    searchInput ||
    filtroActivo !== "todos" ||
    filtroPulso !== "todos" ||
    (filtroGrupo && filtroGrupo !== "todos");

  return (
    <MDBox position="relative" mb={4}>
      <Card
        sx={{
          borderRadius: "16px",
          overflow: "hidden",
          boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
          border: "1px solid",
          borderColor: "divider",
          backgroundColor: "background.paper",
          transition: "box-shadow 0.3s ease",
          "&:hover": {
            boxShadow: "0 12px 32px rgba(0,0,0,0.15)",
          },
        }}
      >
        {/* Encabezado con gradiente verde WhatsApp */}
        <MDBox
          p={3}
          sx={{
            background: `linear-gradient(135deg, #075e54 0%, #128c7e 100%)`,
            borderBottom: "1px solid rgba(255,255,255,0.15)",
            position: "relative",
            "&::after": {
              content: '""',
              position: "absolute",
              top: 0,
              right: 0,
              bottom: 0,
              left: 0,
              background: "rgba(255,255,255,0.05)",
              pointerEvents: "none",
            },
          }}
        >
          <Grid container alignItems="center" justifyContent="space-between" spacing={2}>
            <Grid item xs={12} md={6}>
              <MDBox display="flex" alignItems="center" gap={2.5}>
                <Box
                  sx={{
                    backgroundColor: "rgba(255,255,255,0.15)",
                    borderRadius: "14px",
                    p: 1.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backdropFilter: "blur(8px)",
                  }}
                >
                  <WhatsApp sx={{ color: "white", fontSize: 32 }} />
                </Box>
                <Box>
                  <MDTypography
                    variant="h4"
                    color="white"
                    fontWeight="bold"
                    sx={{
                      lineHeight: 1.2,
                      letterSpacing: "0.3px",
                      textShadow: "0 2px 4px rgba(0,0,0,0.2)",
                    }}
                  >
                    Gestión de WhatsApp para Copropiedades
                  </MDTypography>
                  <MDTypography
                    variant="body2"
                    color="rgba(255,255,255,0.9)"
                    sx={{
                      mt: 0.5,
                      display: "block",
                      fontWeight: 400,
                      textShadow: "0 1px 2px rgba(0,0,0,0.1)",
                    }}
                  >
                    Configuración de alertas y notificaciones WhatsApp para Copropiedades
                  </MDTypography>
                </Box>
              </MDBox>
            </Grid>

            <Grid item xs={12} md={6}>
              <MDBox display="flex" justifyContent="flex-end" alignItems="center" gap={2}>
                <Box
                  sx={{
                    backgroundColor: "rgba(255,255,255,0.1)",
                    borderRadius: "20px",
                    px: 2,
                    py: 1,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <AccessTime sx={{ color: "rgba(255,255,255,0.8)", fontSize: 18 }} />
                  <MDTypography variant="caption" color="white" sx={{ fontWeight: 500 }}>
                    {formatTime()}
                  </MDTypography>
                </Box>

                <Tooltip title="Crear nueva configuración" arrow>
                  <MDButton
                    variant="contained"
                    color="success"
                    size="small"
                    onClick={onCreateClick}
                    startIcon={<AddBusiness />}
                    sx={{
                      borderRadius: "10px",
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: "0.875rem",
                      px: 2.5,
                      py: 1,
                      backgroundColor: "#25D366",
                      "&:hover": {
                        backgroundColor: "#20BD59",
                        boxShadow: "0 4px 12px rgba(37, 211, 102, 0.3)",
                      },
                    }}
                  >
                    Nueva Configuración
                  </MDButton>
                </Tooltip>

                <Tooltip title="Actualizar datos" arrow>
                  <MDButton
                    variant="contained"
                    color="white"
                    size="small"
                    onClick={onRefresh}
                    disabled={loading}
                    startIcon={<Refresh />}
                    sx={{
                      borderRadius: "10px",
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: "0.875rem",
                      px: 2.5,
                      py: 1,
                      backgroundColor: "white",
                      color: "#075e54",
                      "&:hover": {
                        backgroundColor: "rgba(255,255,255,0.95)",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                      },
                      "&.Mui-disabled": {
                        backgroundColor: "rgba(255,255,255,0.5)",
                        color: "rgba(0,0,0,0.4)",
                      },
                    }}
                  >
                    {loading ? "Actualizando..." : "Actualizar"}
                  </MDButton>
                </Tooltip>
              </MDBox>
            </Grid>
          </Grid>
        </MDBox>

        {/* Contenido principal */}
        <MDBox p={3.5}>
          <MDBox display="flex" alignItems="center" gap={1} mb={2.5}>
            <FilterAlt sx={{ color: "#075e54", fontSize: 22 }} />
            <MDTypography variant="h6" color="textPrimary" fontWeight="medium">
              Filtros y Búsqueda
            </MDTypography>
            {hasActiveFilters && (
              <Chip
                label="Filtros activos"
                size="small"
                color="primary"
                onDelete={clearAllFilters}
                sx={{
                  ml: 1,
                  fontWeight: 600,
                  fontSize: "0.75rem",
                  height: "26px",
                  "& .MuiChip-deleteIcon": {
                    fontSize: "16px",
                  },
                }}
              />
            )}
          </MDBox>

          <Grid container spacing={2.5} alignItems="flex-start">
            {/* Buscador */}
            <Grid item xs={12} md={3}>
              <MDTypography variant="caption" color="text.secondary" fontWeight="500" mb={0.5}>
                BÚSQUEDA
              </MDTypography>
              <Paper
                elevation={0}
                sx={{
                  borderRadius: "12px",
                  overflow: "hidden",
                  border: "2px solid",
                  borderColor: searchInput ? "#075e54" : "divider",
                  "&:hover": {
                    borderColor: "#075e54",
                  },
                  transition: "all 0.2s ease",
                }}
              >
                <TextField
                  fullWidth
                  placeholder="Buscar por comercio, ID, terminal o nombre de terminal..."
                  value={searchInput}
                  onChange={handleSearchChange}
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ color: "#075e54", fontSize: 22 }} />
                      </InputAdornment>
                    ),
                    endAdornment: searchInput && (
                      <InputAdornment position="end">
                        <MDButton
                          size="small"
                          variant="text"
                          color="primary"
                          onClick={clearSearch}
                          sx={{
                            minWidth: "auto",
                            p: 0.5,
                            mr: 0.5,
                            fontSize: "18px",
                            fontWeight: "bold",
                            "&:hover": { backgroundColor: alpha("#075e54", 0.1) },
                          }}
                        >
                          ✕
                        </MDButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiInputBase-root": {
                      height: "56px",
                      fontSize: "1rem",
                    },
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        border: "none",
                      },
                    },
                  }}
                />
              </Paper>
            </Grid>

            {/* Filtro por estado */}
            <Grid item xs={12} md={2.5}>
              <MDTypography variant="caption" color="text.secondary" fontWeight="500" mb={0.5}>
                ESTADO
              </MDTypography>
              <ToggleButtonGroup
                value={filtroActivo}
                exclusive
                onChange={handleActivoChange}
                aria-label="filtro estado"
                fullWidth
                sx={{
                  height: "56px",
                  backgroundColor: "#f5f5f5",
                  borderRadius: "12px",
                  p: 0.5,
                  "& .MuiToggleButtonGroup-grouped": {
                    border: "none",
                    borderRadius: "10px !important",
                    mx: 0.3,
                    fontSize: "0.85rem",
                    "&.Mui-selected": {
                      backgroundColor: "white",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    },
                  },
                }}
              >
                <ToggleButton value="todos" sx={{ textTransform: "none" }}>
                  <Storage sx={{ mr: 0.5, fontSize: 18 }} /> Todos
                </ToggleButton>
                <ToggleButton value="activos" sx={{ textTransform: "none" }}>
                  <CheckCircle sx={{ mr: 0.5, fontSize: 18, color: "#4caf50" }} /> Activos
                </ToggleButton>
                <ToggleButton value="inactivos" sx={{ textTransform: "none" }}>
                  <Cancel sx={{ mr: 0.5, fontSize: 18, color: "#f44336" }} /> Inactivos
                </ToggleButton>
              </ToggleButtonGroup>
            </Grid>

            {/* Filtro por pulso */}
            <Grid item xs={12} md={2.5}>
              <MDTypography variant="caption" color="text.secondary" fontWeight="500" mb={0.5}>
                PULSO
              </MDTypography>
              <ToggleButtonGroup
                value={filtroPulso}
                exclusive
                onChange={handlePulsoChange}
                aria-label="filtro pulso"
                fullWidth
                sx={{
                  height: "56px",
                  backgroundColor: "#f5f5f5",
                  borderRadius: "12px",
                  p: 0.5,
                  "& .MuiToggleButtonGroup-grouped": {
                    border: "none",
                    borderRadius: "10px !important",
                    mx: 0.3,
                    fontSize: "0.85rem",
                    "&.Mui-selected": {
                      backgroundColor: "white",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    },
                  },
                }}
              >
                <ToggleButton value="todos" sx={{ textTransform: "none" }}>
                  <Storage sx={{ mr: 0.5, fontSize: 18 }} /> Todos
                </ToggleButton>
                <ToggleButton value="conPulso" sx={{ textTransform: "none" }}>
                  <Timeline sx={{ mr: 0.5, fontSize: 18, color: "#2196f3" }} /> Con Pulso
                </ToggleButton>
                <ToggleButton value="sinPulso" sx={{ textTransform: "none" }}>
                  <Cancel sx={{ mr: 0.5, fontSize: 18, color: "#9e9e9e" }} /> Sin Pulso
                </ToggleButton>
              </ToggleButtonGroup>
            </Grid>

            {/* Filtro por grupo NOC */}
            <Grid item xs={12} md={4}>
              <MDTypography variant="caption" color="text.secondary" fontWeight="500" mb={0.5}>
                GRUPO NOC
              </MDTypography>
              <Autocomplete
                options={grupoOptions}
                getOptionLabel={(option) => option.label}
                value={selectedGrupo}
                onChange={handleGrupoChange}
                inputValue={grupoInputValue}
                onInputChange={(event, newInputValue) => setGrupoInputValue(newInputValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Buscar grupo..."
                    variant="outlined"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <Group sx={{ color: "#075e54", fontSize: 22 }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiInputBase-root": {
                        height: "56px",
                        fontSize: "1rem",
                      },
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "12px",
                        "& fieldset": {
                          borderColor: "divider",
                          borderWidth: "2px",
                        },
                        "&:hover fieldset": {
                          borderColor: "#075e54",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#075e54",
                        },
                      },
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <MenuItem {...props} sx={{ py: 2, px: 2.5 }}>
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      width="100%"
                    >
                      <Box display="flex" alignItems="center" gap={1.5}>
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: "4px",
                            backgroundColor: option.color,
                          }}
                        />
                        <Typography variant="body1" fontWeight="500" sx={{ fontSize: "0.95rem" }}>
                          {option.label}
                        </Typography>
                      </Box>
                      {option.value !== "todos" && (
                        <Chip
                          label={option.value}
                          size="small"
                          sx={{
                            height: "24px",
                            fontSize: "0.75rem",
                            backgroundColor: alpha(option.color, 0.1),
                            color: option.color,
                            fontWeight: 600,
                          }}
                        />
                      )}
                    </Box>
                  </MenuItem>
                )}
              />
            </Grid>
          </Grid>

          {/* Filtros activos */}
          {hasActiveFilters && (
            <MDBox mt={2.5} display="flex" alignItems="center" gap={1} flexWrap="wrap">
              <MDTypography variant="caption" color="text.secondary" fontWeight="500">
                Filtros aplicados:
              </MDTypography>
              {searchInput && (
                <Chip
                  label={`Búsqueda: "${searchInput}"`}
                  size="small"
                  onDelete={() => onFiltroChange("busqueda", "")}
                  sx={{
                    height: "28px",
                    backgroundColor: alpha("#075e54", 0.1),
                    color: "#075e54",
                    fontWeight: 500,
                    fontSize: "0.75rem",
                  }}
                />
              )}
              {filtroActivo !== "todos" && (
                <Chip
                  icon={filtroActivo === "activos" ? <CheckCircle /> : <Cancel />}
                  label={filtroActivo === "activos" ? "Activos" : "Inactivos"}
                  size="small"
                  onDelete={() => onFiltroChange("activo", "todos")}
                  color={filtroActivo === "activos" ? "success" : "error"}
                  sx={{ height: "28px", fontWeight: 500, fontSize: "0.75rem" }}
                />
              )}
              {filtroPulso !== "todos" && (
                <Chip
                  icon={filtroPulso === "conPulso" ? <Timeline /> : <Cancel />}
                  label={filtroPulso === "conPulso" ? "Con Pulso" : "Sin Pulso"}
                  size="small"
                  onDelete={() => onFiltroChange("pulso", "todos")}
                  color={filtroPulso === "conPulso" ? "info" : "default"}
                  sx={{ height: "28px", fontWeight: 500, fontSize: "0.75rem" }}
                />
              )}
              {filtroGrupo && filtroGrupo !== "todos" && (
                <Chip
                  label={`Grupo: ${filtroGrupo}`}
                  size="small"
                  onDelete={() => onFiltroChange("grupo", "todos")}
                  sx={{
                    height: "28px",
                    backgroundColor: alpha(getGrupoColor(filtroGrupo), 0.1),
                    color: getGrupoColor(filtroGrupo),
                    fontWeight: 500,
                    fontSize: "0.75rem",
                  }}
                />
              )}
            </MDBox>
          )}
        </MDBox>
      </Card>
    </MDBox>
  );
};

HeaderWhatsappCop.propTypes = {
  onRefresh: PropTypes.func,
  onCreateClick: PropTypes.func,
  loading: PropTypes.bool,
  filtros: PropTypes.shape({
    grupo: PropTypes.string,
    activo: PropTypes.string,
    pulso: PropTypes.string,
    busqueda: PropTypes.string,
  }),
  onFiltroChange: PropTypes.func,
  gruposNOC: PropTypes.array,
};

HeaderWhatsappCop.defaultProps = {
  onRefresh: null,
  onCreateClick: null,
  loading: false,
  filtros: { grupo: "todos", activo: "todos", pulso: "todos", busqueda: "" },
  onFiltroChange: null,
  gruposNOC: [],
};

export default HeaderWhatsappCop;
